import {
  useInitSelectedDateOnDiaryPageOnly,
  useNotifyDeleteMealFailed,
} from "../hooks"
import {
  AIScanBoxSkeleton,
  BannerOverlay,
  HomeMain,
  HomeMainInner,
  RecentMealDetailsOverlay,
  MealSectionSkeleton,
  NoMealsBox,
  NoMealsText,
  NoMealsTitle,
  StyledHome,
} from "../styled"
import { FlexBox } from "components/Common/FlexBox"
import MealSection from "./MealSection"
import React, { Fragment, useEffect, useState } from "react"
import { useSetPageTitle } from "libs/ga"
import {
  useCheckShouldShowTutorial,
  useDisplayWeightChangedNotification,
  useGoal,
  useIsTutorialShowed,
  useTutorialBackPath,
  useUser,
} from "features/onboarding/hooks"
import { useResetSearchFoodsState } from "features/meal/hooks"
import { useResetMealDetailsBackPath } from "features/meal/hooks/mealDetailsHooks"
import { usePaywall } from "features/payment/hooks"
import DiaryNavigation from "./DiaryNavigation"
import WeightChangedNotification from "./WeightChangedNotification"
import { useAppSelector } from "app/hooks"
import { useLogBrazeEvent } from "libs/braze"
import { useNavigate } from "react-router-dom"
import PATHS from "router/paths"
import PaywallTriggerWrapper from "../Paywall/WebViewPaywallTrigerer"
import AIScanBox from "features/home/Diary/AIScanBox"
import useShowFitBanner from "features/notification/useShowFitBanner"
import FixedActionButton from "./FixedActionButton"
import ActionsModal from "./ActionsModal"
import DailyProgress from "./DailyProgress"
import { useSuggestedMeals } from "features/meal/hooks/useSuggestedMeals"
import RecentMeals from "./RecentMeals"
import NoMealsLoggedIcon from "components/Icons/NoMealsLoggedIcon"
import useLogMealFromMeal from "features/meal/hooks/logMeal/useLogMealFromMeal"
import { MealType } from "model/food/types"
import MealDetails from "components/mealDetails/MealDetails"
import WaterSection from "./WaterSection"
import dayjs from "dayjs"
import PopConfirm from "components/PopConfirm"
import WaterInput from "components/inputs/WaterInput"
import { useDailyWater } from "features/water/hooks/useDailyWater"
import { useUpdateWater } from "features/water/hooks/useUpdateWater"
import { useBodyDisplayUnits } from "features/profile/hooks/body"
import {
  getDisplayWaterVolume,
  getWaterVolumeInMl,
  getWaterUnit,
} from "utils/waterVolumeConverter"

const Home = () => {
  const [refreshTime, setRefreshTime] = useState(0)
  const [isWaterValid, setIsWaterValid] = useState(true)

  const foodInFitVersion = useAppSelector(
    (state) => state.notification.foodInFitVersion,
  )

  const shouldShowCtaFloatButton = foodInFitVersion === "v1"

  useInitSelectedDateOnDiaryPageOnly()
  useUser()
  useShowFitBanner()
  useSetPageTitle("Diary")
  useResetSearchFoodsState()
  useGoal()

  const isDiary = true
  const {
    suggestedMeals,
    loadMore,
    getSuggestedMealsLoading,
    hasMore,
    isNoSuggestedMeals,
  } = useSuggestedMeals(isDiary)

  const [isActionModalOpen, setIsActionModalOpen] = useState(false)
  const [isWaterEditModalOpen, setIsWaterEditModalOpen] = useState(false)
  const [editWaterValue, setEditWaterValue] = useState(0)
  const [hasUserFocusedWaterInput, setHasUserFocusedWaterInput] =
    useState(false)
  const meals = useAppSelector((state) => state.home.meals)
  const getUserPlanLoading = useAppSelector(
    (state) => state.payment.getUserPlanLoading,
  )
  const isNoMeals = useAppSelector((state) => state.home.isNoMeals)
  const [swipedId, setSwipedId] = useState<string | null>(null)
  useNotifyDeleteMealFailed()
  useResetMealDetailsBackPath()

  const {
    checkToShowPaywallWhenOpeningActions,
    shouldShowPaywallInWebView,
    hasAIScanEntitlement,
    hasBarcodeScanningEntitlement,
  } = usePaywall()

  const { updateIsTutorialShowed } = useIsTutorialShowed()
  useEffect(() => {
    updateIsTutorialShowed(false)
  }, [])

  const { logBrazeTryAIStarted } = useLogBrazeEvent()
  const shouldShowFitBanner = useAppSelector(
    (state) => state.notification.shouldShowFitBanner,
  )

  const { isDisplayWeightChangedNotification } =
    useDisplayWeightChangedNotification()

  // Water edit modal
  const selectedDate = useAppSelector((state) => state.home.selectedDate)
  const { dailyWaterTotal } = useDailyWater()
  const { bodyDisplayUnits } = useBodyDisplayUnits()
  const displayWaterValue = getDisplayWaterVolume(
    bodyDisplayUnits,
    dailyWaterTotal,
  )
  const { handleUpdateWater, updateWaterLoading } = useUpdateWater(() => {
    setIsWaterEditModalOpen(false)
  })

  const onOpenWaterEditModal = () => {
    setEditWaterValue(displayWaterValue)
    setHasUserFocusedWaterInput(false)
    setIsWaterEditModalOpen(true)
  }

  const onConfirmWaterEdit = () => {
    const date = selectedDate
      ? dayjs(selectedDate).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD")
    const amountInMl = getWaterVolumeInMl(bodyDisplayUnits, editWaterValue)
    handleUpdateWater({ amount: amountInMl, date })
  }

  useEffect(() => {
    if (isWaterEditModalOpen && !hasUserFocusedWaterInput) {
      setEditWaterValue(displayWaterValue)
    }
  }, [displayWaterValue, isWaterEditModalOpen, hasUserFocusedWaterInput])

  const { isShowTutorial, checkShowTutorialLoading } =
    useCheckShouldShowTutorial()

  const navigate = useNavigate()
  const { updateTutorialBackPath } = useTutorialBackPath()

  const onTryAI = () => {
    if (checkShowTutorialLoading || getUserPlanLoading) {
      return
    }

    if (!hasAIScanEntitlement) {
      return
    }

    logBrazeTryAIStarted()

    if (isShowTutorial) {
      const currentPath = `${location.pathname}${location.search}`
      updateTutorialBackPath(currentPath)
    }

    const path = isShowTutorial ? PATHS.app.meal.tutorial : PATHS.app.meal.scan
    navigate(path)
  }

  const isFirstLoadDiary = useAppSelector(
    (state) => state.home.isFirstLoadDiary,
  )

  const logMealSuccessCallback = () => {
    onCloseMealDetails()
    setRefreshTime((prev) => prev + 1)
  }

  const { handleLogMealFromMeal } = useLogMealFromMeal(logMealSuccessCallback)
  const [recentMealDetails, setRecentMealDetails] = useState<MealType | null>()

  const onSelectRecentMeal = (meal: MealType) => {
    setRecentMealDetails({ ...meal, type: undefined })
  }

  const onCloseMealDetails = () => {
    setRecentMealDetails(null)
  }

  return (
    <StyledHome
      style={
        {
          "--fit-banner-height": shouldShowFitBanner ? "58px" : "0px",
          "--profile-header-height": "0px",
        } as React.CSSProperties
      }
    >
      {recentMealDetails && (
        <RecentMealDetailsOverlay>
          <MealDetails
            mealDetails={recentMealDetails}
            onBack={onCloseMealDetails}
            logMealSuccessCallback={logMealSuccessCallback}
          />
        </RecentMealDetailsOverlay>
      )}

      <PopConfirm
        type="modal"
        disabled={updateWaterLoading || !isWaterValid}
        loading={updateWaterLoading}
        isOpen={isWaterEditModalOpen}
        title="Edit Total Amount"
        onConfirm={onConfirmWaterEdit}
        onCancel={() => {
          setIsWaterEditModalOpen(false)
          setTimeout(() => {
            setEditWaterValue(displayWaterValue)
            setIsWaterValid(true)
          }, 500)
        }}
        titleStyle={{ lineHeight: "22px" }}
        cancelText="Cancel"
        confirmText="Save change"
      >
        <WaterInput
          isReInitValue={!!isWaterEditModalOpen}
          label="Water Intake"
          value={editWaterValue}
          onChange={setEditWaterValue}
          onFocus={() => setHasUserFocusedWaterInput(true)}
          disabled={updateWaterLoading}
          unit={getWaterUnit(bodyDisplayUnits)}
          showPlusPrefix={false}
          setIsDisplayValueValid={setIsWaterValid}
        />
      </PopConfirm>

      <BannerOverlay $height={hasAIScanEntitlement ? 312 : 332} />

      <DiaryNavigation refreshTime={refreshTime} />

      <HomeMain>
        <DailyProgress />
        <HomeMainInner>
          <FlexBox $direction="column" $gap={12}>
            <WaterSection onEditClick={onOpenWaterEditModal} />

            {isFirstLoadDiary ? (
              <AIScanBoxSkeleton />
            ) : (
              !hasAIScanEntitlement && (
                <PaywallTriggerWrapper isCheckingAIScan>
                  <AIScanBox
                    isLocked={!hasAIScanEntitlement}
                    onClick={onTryAI}
                  />
                </PaywallTriggerWrapper>
              )
            )}

            {isNoMeals && (
              <FlexBox
                $direction="column"
                $gap={12}
                $alignItems="center"
                $justify="center"
              >
                <NoMealsBox>
                  <NoMealsTitle>no logged meal yet</NoMealsTitle>
                  <NoMealsText>
                    Let’s start logging first meal of the day!
                  </NoMealsText>
                </NoMealsBox>
                {isNoSuggestedMeals && <NoMealsLoggedIcon />}

                {!!suggestedMeals.length && (
                  <RecentMeals
                    meals={suggestedMeals}
                    loadMore={loadMore}
                    loading={getSuggestedMealsLoading}
                    hasMore={hasMore}
                    onSelectMeal={onSelectRecentMeal}
                    handleLogMealFromMeal={handleLogMealFromMeal}
                  />
                )}
              </FlexBox>
            )}

            {isFirstLoadDiary ? (
              <FlexBox $gap={12} $direction="column">
                <MealSectionSkeleton />
                <MealSectionSkeleton />
                <MealSectionSkeleton />
                <MealSectionSkeleton />
              </FlexBox>
            ) : (
              <Fragment>
                <MealSection
                  swipedId={swipedId}
                  setSwipedId={setSwipedId}
                  meals={meals}
                />
              </Fragment>
            )}
          </FlexBox>
        </HomeMainInner>
      </HomeMain>

      {isDisplayWeightChangedNotification && <WeightChangedNotification />}

      {shouldShowCtaFloatButton && (
        <FixedActionButton
          onOpenActionsModal={() => setIsActionModalOpen(true)}
          checkToShowPaywallWhenOpeningActions={
            checkToShowPaywallWhenOpeningActions
          }
          shouldShowPaywallInWebView={shouldShowPaywallInWebView}
        />
      )}

      {isActionModalOpen && (
        <ActionsModal
          onClose={() => setIsActionModalOpen(false)}
          hasAIScanEntitlement={hasAIScanEntitlement}
          hasBarcodeScanningEntitlement={hasBarcodeScanningEntitlement}
        />
      )}
    </StyledHome>
  )
}

export default Home
